<template>
  <div ref="rootRef" class="behaviour-positive-behaviour-select">
    <CustomSelect
      ref="customSelectRef"
      :input-id="resolvedInputId"
      :accessible-label="accessibleLabel"
      :hide-floating-label="hideFloatingLabel"
      :labelled-by="labelledBy"
      :model-value="normalizedModelValue"
      :class="classes"
      :options="visibleOptions"
      :clearable="clearable"
      :placeholder="resolvedPlaceholder"
      :filterable="false"
      :autoscroll="false"
      :reduce="reduce"
      :selectable="isSelectable"
      :invalid-feedback="invalidFeedback"
      :disabled="isDisabled"
      :append-dropdown-to-body="appendDropdownToBody"
      :append-dropdown-fixed-position="appendDropdownFixedPosition"
      :close-on-select="true"
      :data-testid="dataTestid"
      @search="onSearch"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template #list-footer>
        <div
          v-if="state.isProcessing || showLoadingLabel"
          class="p-2 text-center text-muted small"
        >
          {{ $t("common.loading_more") }}
        </div>
      </template>
      <template #no-options>
        <span class="text-black-50 pm-1 d-block">
          {{ emptyOptionsMessage }}
        </span>
      </template>
    </CustomSelect>
    <output
      v-if="state.isProcessing"
      class="visually-hidden"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ $t("common.loading_more") }}
    </output>
  </div>
</template>

<script>
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import {
  dedupeLazySelectOptions,
  getLazySelectDropdownMenu,
  restoreLazySelectDropdownScroll,
  scheduleLazySelectLoadMore
} from "@/helpers/behaviourLazySelectScroll.js"
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const SEARCH_DEBOUNCE_MS = 320
const PER_PAGE = 25

export default {
  name: "BehaviourPositiveBehaviourLazySelect",
  components: { CustomSelect },
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: null
    },
    /** Required to load positive behaviours for the selected core value. */
    coreValueId: {
      type: [String, Number],
      default: null
    },
    selectedOption: {
      type: Object,
      default: null
    },
    reduce: {
      type: Function,
      default: (option) =>
        option && option.value != null ? option.value : null
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    accessibleLabel: { type: String, default: "" },
    hideFloatingLabel: { type: Boolean, default: false },
    labelledBy: { type: String, default: "" },
    invalidFeedback: { type: [String, Array], default: "" },
    classes: { type: [String, Object, Array], default: "" },
    appendDropdownToBody: { type: Boolean, default: false },
    appendDropdownFixedPosition: { type: Boolean, default: false },
    dataTestid: { type: String, default: "" },
    statusFilter: {
      type: String,
      default: "all"
    },
    coreValueLabel: {
      type: String,
      default: ""
    },
    prefetchOnMount: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "changed", "option-selected"],
  setup(props, { emit }) {
    const store = useStore()
    const { t } = useI18n()
    const rootRef = ref(null)
    const customSelectRef = ref(null)
    const instance = getCurrentInstance()

    let detachScroll = null
    let searchTimeoutId = null
    let loadMoreTimeoutId = null

    const state = reactive({
      options: [],
      page: 1,
      lastPage: null,
      searchQuery: "",
      isProcessing: false,
      hasFetched: false,
      isOpen: false
    })

    const resolvedInputId = computed(
      () => props.inputId || `behaviour-pb-lazy-${instance?.uid ?? ""}`
    )

    const hasCoreValue = computed(
      () => props.coreValueId != null && props.coreValueId !== ""
    )

    const isDisabled = computed(() => props.disabled || !hasCoreValue.value)

    const normalizedModelValue = computed(() => {
      const v = props.modelValue
      if (v == null || v === "") return v
      if (typeof v === "object") return v
      return String(v)
    })

    const isLastPage = computed(
      () => state.lastPage != null && state.lastPage <= state.page
    )

    const showLoadingLabel = computed(
      () => state.hasFetched && !isLastPage.value && !state.isProcessing
    )

    const resolvedPlaceholder = computed(() => {
      if (props.placeholder) return props.placeholder
      if (!hasCoreValue.value) {
        return t("behaviour_manage.select_core_value_first_placeholder", {
          coreValue: props.coreValueLabel
        })
      }
      return t("common.select")
    })

    const emptyOptionsMessage = computed(() => {
      if (!hasCoreValue.value) {
        return t("behaviour_manage.select_core_value_first_placeholder", {
          coreValue: props.coreValueLabel
        })
      }
      return t("common.sorry_no_matching_options")
    })

    const visibleOptions = computed(() => {
      let opts = [...(state.options || [])]

      const sel = props.selectedOption
      if (
        sel &&
        sel.value != null &&
        sel.value !== "" &&
        !opts.some((opt) => String(opt?.value) === String(sel.value))
      ) {
        const rawLabel = sel.label
        const labelStr =
          rawLabel != null && String(rawLabel).trim() !== ""
            ? String(rawLabel)
            : String(sel.value)
        opts = [
          {
            label: labelStr,
            value: String(sel.value),
            points: sel.points,
            awardDescription: sel.awardDescription
          },
          ...opts
        ]
      }

      return opts
    })

    const isSelectable = () => true

    const resetListState = () => {
      state.options = []
      state.page = 1
      state.lastPage = null
      state.hasFetched = false
      state.searchQuery = ""
    }

    const fetchPage = async ({ append = false } = {}) => {
      if (!hasCoreValue.value) {
        if (!append) state.options = []
        return
      }

      const requestCoreValueId = String(props.coreValueId ?? "")

      try {
        state.isProcessing = true
        const { rows, pagination } = await store.dispatch(
          "behaviourSettings/fetchPositiveBehavioursList",
          {
            page: state.page,
            perPage: PER_PAGE,
            search: state.searchQuery,
            statusFilter: props.statusFilter || "all",
            coreValueId: props.coreValueId
          }
        )
        const newOptions = (rows || [])
          .filter((row) => row?.id != null)
          .filter(
            (row) => String(row?.coreValueId ?? "") === requestCoreValueId
          )
          .map((row) => ({
            label: String(row?.name ?? row?.id ?? ""),
            value: String(row.id),
            points: row?.points,
            awardDescription: row?.awardDescription,
            coreValueId: row?.coreValueId
          }))
        if (append) {
          const menu = getLazySelectDropdownMenu(customSelectRef)
          const scrollTop = menu?.scrollTop ?? 0
          const uniqueOptions = dedupeLazySelectOptions(
            state.options,
            newOptions
          )
          state.options.push(...uniqueOptions)
          await restoreLazySelectDropdownScroll(customSelectRef, scrollTop)
        } else {
          state.options = newOptions
        }
        state.lastPage = Number(pagination?.total_pages) || 1
        state.hasFetched = true
      } catch {
        if (!append) state.options = []
      } finally {
        state.isProcessing = false
      }
    }

    const onSearch = (query) => {
      if (!hasCoreValue.value) return
      const q = query == null ? "" : String(query)
      if (q.replace(/\s+/g, "").length === 1) return
      if (searchTimeoutId) clearTimeout(searchTimeoutId)
      searchTimeoutId = setTimeout(() => {
        state.searchQuery = q
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }, SEARCH_DEBOUNCE_MS)
    }

    const onChange = (value) => {
      emit("update:modelValue", value)
      emit("changed", value)
      const matched = (visibleOptions.value || []).find(
        (opt) => opt && String(opt.value) === String(value)
      )
      if (matched) emit("option-selected", { ...matched })
    }

    const handleScroll = (event) => {
      const menu = event.target
      if (!menu || typeof menu.scrollTop !== "number") return
      if (!menu.classList?.contains?.("vs__dropdown-menu")) return
      if (!state.options.length || isLastPage.value || state.isProcessing) {
        return
      }
      if (menu.offsetHeight + menu.scrollTop + 8 >= menu.scrollHeight) {
        loadMore()
      }
    }

    const loadMore = () => {
      scheduleLazySelectLoadMore({
        isBlocked: () =>
          state.isProcessing || isLastPage.value || !hasCoreValue.value,
        onLoad: () => {
          state.page += 1
          fetchPage({ append: true })
        },
        getTimeoutId: () => loadMoreTimeoutId,
        setTimeoutId: (id) => {
          loadMoreTimeoutId = id
        }
      })
    }

    const removeScrollListener = () => {
      if (typeof detachScroll === "function") detachScroll()
      detachScroll = null
    }

    const attachScrollListener = () => {
      removeScrollListener()
      if (props.appendDropdownToBody) {
        nextTick(() => {
          requestAnimationFrame(() => {
            const menu =
              customSelectRef.value?.getDropdownMenuElement?.() || null
            if (menu?.addEventListener) {
              menu.addEventListener("scroll", handleScroll, { passive: true })
              detachScroll = () => {
                menu.removeEventListener("scroll", handleScroll)
              }
            }
          })
        })
      } else {
        const root = rootRef.value
        if (root?.addEventListener) {
          root.addEventListener("scroll", handleScroll, {
            capture: true,
            passive: true
          })
          detachScroll = () => {
            root.removeEventListener("scroll", handleScroll, { capture: true })
          }
        }
      }
    }

    const onOpenSelect = () => {
      state.isOpen = true
      if (!state.hasFetched && !isDisabled.value) {
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }
      attachScrollListener()
    }

    const onCloseSelect = () => {
      state.isOpen = false
      removeScrollListener()
    }

    watch(
      () => props.coreValueId,
      (newVal, oldVal) => {
        if (String(newVal ?? "") === String(oldVal ?? "")) return
        resetListState()
      }
    )

    watch(
      () => props.statusFilter,
      () => {
        if (state.hasFetched && hasCoreValue.value) {
          state.page = 1
          state.lastPage = null
          fetchPage({ append: false })
        }
      }
    )

    onMounted(() => {
      if (
        props.prefetchOnMount &&
        hasCoreValue.value &&
        !state.hasFetched &&
        !isDisabled.value
      ) {
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }
    })

    onUnmounted(() => {
      removeScrollListener()
      if (searchTimeoutId) clearTimeout(searchTimeoutId)
      if (loadMoreTimeoutId) clearTimeout(loadMoreTimeoutId)
    })

    return {
      rootRef,
      customSelectRef,
      state,
      resolvedInputId,
      normalizedModelValue,
      visibleOptions,
      showLoadingLabel,
      isDisabled,
      resolvedPlaceholder,
      emptyOptionsMessage,
      isSelectable,
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect
    }
  }
}
</script>
